import React, { useState, useEffect, useCallback } from 'react';
import { useVehicleSimulator } from './hooks/useVehicleSimulator';
import { LaunchScreen } from './components/LaunchScreen';
import { SafetyTermsModal } from './components/SafetyTermsModal';
import { DrivingDashboard } from './components/DrivingDashboard';
import { SoundCatalogueSheet } from './components/SoundCatalogueSheet';
import { AccountDrawer } from './components/AccountDrawer';
import { AdminPortal } from './components/AdminPortal';
import { api, ApiUser } from './services/api';

export const App: React.FC = () => {
  // Vehicle Powertrain & Audio Simulation
  const {
    vehicleState,
    speedUnit,
    setSpeedUnit,
    startEngine,
    stopEngine,
    setDemoMode,
    setDemoSpeed,
    setDemoAcceleration,
    shiftUp,
    shiftDown,
    setTransmissionMode,
    setDriveMode,
    setEngineProfile,
    setCharacterMode,
    setVolume,
    toggleMute,
  } = useVehicleSimulator();

  // App Lifecycle States
  const [hasLaunched, setHasLaunched] = useState<boolean>(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(() => {
    try {
      return localStorage.getItem('revive_terms_accepted') === 'true';
    } catch {
      return false;
    }
  });

  // Modal & Drawer Visibility States
  const [isCatalogueOpen, setIsCatalogueOpen] = useState<boolean>(false);
  const [isAccountOpen, setIsAccountOpen] = useState<boolean>(false);
  const [isAdminOpen, setIsAdminOpen] = useState<boolean>(false);

  // Auth User State
  const [currentUser, setCurrentUser] = useState<ApiUser | null>(null);

  // Fetch current user / session status from backend
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.auth.getMe();
      if (res.isAuthenticated && res.user) {
        setCurrentUser(res.user);
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Route / Hash listener: support direct navigation to #admin or /admin
  useEffect(() => {
    const checkAdminRoute = () => {
      const hash = window.location.hash;
      const pathname = window.location.pathname;
      if (hash === '#admin' || pathname === '/admin') {
        setIsAdminOpen(true);
      }
    };

    checkAdminRoute();
    window.addEventListener('hashchange', checkAdminRoute);
    return () => {
      window.removeEventListener('hashchange', checkAdminRoute);
    };
  }, []);

  const handleTermsAccepted = () => {
    try {
      localStorage.setItem('revive_terms_accepted', 'true');
    } catch {
      // Safe fallback
    }
    setHasAcceptedTerms(true);
  };

  const handleCloseAdmin = () => {
    setIsAdminOpen(false);
    if (window.location.hash === '#admin') {
      window.history.pushState(
        '',
        document.title,
        window.location.pathname + window.location.search
      );
    }
  };

  return (
    <div className="w-screen h-screen overflow-hidden bg-[#06070a] font-display text-slate-100 select-none">
      {/* 1. Launch Screen: 1-second automotive signature chime & animation */}
      {!hasLaunched && (
        <LaunchScreen onComplete={() => setHasLaunched(true)} />
      )}

      {/* 2. Safety & Terms Modal (First-time onboarding gate) */}
      {hasLaunched && !hasAcceptedTerms && (
        <SafetyTermsModal onAccept={handleTermsAccepted} />
      )}

      {/* 3. Automotive Driving Dashboard */}
      {hasLaunched && (
        <DrivingDashboard
          vehicleState={vehicleState}
          speedUnit={speedUnit}
          onSetSpeedUnit={setSpeedUnit}
          onStartEngine={startEngine}
          onStopEngine={stopEngine}
          onShiftUp={shiftUp}
          onShiftDown={shiftDown}
          onSetTransmissionMode={setTransmissionMode}
          onSetDriveMode={setDriveMode}
          onSetEngineProfile={setEngineProfile}
          onSetCharacterMode={setCharacterMode}
          onSetVolume={setVolume}
          onToggleMute={toggleMute}
          currentUser={currentUser}
          onOpenCatalogue={() => setIsCatalogueOpen(true)}
          onOpenAccount={() => setIsAccountOpen(true)}
          onOpenAdmin={() => setIsAdminOpen(true)}
        />
      )}

      {/* 4. Sound Catalogue Sheet (Free guest sounds vs locked packages) */}
      <SoundCatalogueSheet
        isOpen={isCatalogueOpen}
        onClose={() => setIsCatalogueOpen(false)}
        currentSoundId={vehicleState.engineProfile}
        onSelectSound={(soundId) => {
          setEngineProfile(soundId);
          setIsCatalogueOpen(false);
        }}
      />

      {/* 5. User Account & Authentication Drawer */}
      <AccountDrawer
        isOpen={isAccountOpen}
        onClose={() => setIsAccountOpen(false)}
        currentUser={currentUser}
        onAuthChange={refreshUser}
      />

      {/* 6. Admin Portal (Protected: Simulation Controls, User Mgmt, Sounds, Audit Logs) */}
      <AdminPortal
        isOpen={isAdminOpen}
        onClose={handleCloseAdmin}
        currentUser={currentUser}
        isSimulationActive={vehicleState.isDemoMode}
        onToggleSimulation={setDemoMode}
        simulationSpeed={vehicleState.speedKmh}
        onSetSimulationSpeed={setDemoSpeed}
        simulationAcceleration={vehicleState.acceleration}
        onSetSimulationAcceleration={setDemoAcceleration}
        onSetEngineProfile={setEngineProfile}
        onSetDriveMode={setDriveMode}
        currentSoundId={vehicleState.engineProfile}
        currentDriveMode={vehicleState.mode}
      />
    </div>
  );
};

export default App;
